import type { Event } from '@/types';
import { normalizeCreditBookingFeePence } from '@/lib/platform-booking-fee';

/** User-facing label for the per-ticket credit recovery line on invoices and RSVP. */
export const BOOKING_FEE_LABEL = 'Booking Fee';

export function isCreditRecoveryEnabledForEvent(event: Event | null | undefined): boolean {
  if (!event) return false;
  // Organiser explicitly turned off "charge booking fee to guest"; do not keep legacy creditCostRecovery.enabled alone.
  if (event.guestCoversCreditCost === false) {
    return false;
  }
  const creditCostRecovery = event.creditCostRecovery || { enabled: false };
  // Feature is enabled if either flag is explicitly true.
  // We no longer default missing guestCoversCreditCost to true.
  return event.guestCoversCreditCost === true || creditCostRecovery.enabled === true;
}

/**
 * Total booking fee in GBP for an invoice covering `billableRsvpCount` RSVPs (matches ensure-invoice / RSVP submit).
 */
export function computeCreditRecoveryTotalPounds(
  event: Event | null | undefined,
  baseTicketTotal: number,
  billableRsvpCount: number,
  bookingFeePence: number,
): number {
  if (!event || baseTicketTotal <= 0 || billableRsvpCount <= 0) return 0;
  if (!isCreditRecoveryEnabledForEvent(event)) return 0;
  const per = normalizeCreditBookingFeePence(bookingFeePence) / 100;
  return billableRsvpCount * per;
}

/** Per billable RSVP when recovery applies; matches total ÷ count from `computeCreditRecoveryTotalPounds`. */
export function getBookingFeePerBillableSlotPounds(
  event: Event | null | undefined,
  baseTicketTotal: number,
  billableRsvpCount: number,
  bookingFeePence: number,
): number {
  const total = computeCreditRecoveryTotalPounds(event, baseTicketTotal, billableRsvpCount, bookingFeePence);
  if (total <= 0 || billableRsvpCount <= 0) return 0;
  return total / billableRsvpCount;
}

/** Single ticket line on public RSVP (one billable RSVP). */
export function getInvoiceCreditRecoveryPounds(
  event: Event | null | undefined,
  ticketPrice: number,
  bookingFeePence: number,
): number {
  return computeCreditRecoveryTotalPounds(event, ticketPrice, ticketPrice > 0 ? 1 : 0, bookingFeePence);
}

export function shouldShowBookingFeeOnRsvp(
  event: Event | null | undefined,
  ticketPrice: number,
  inviterPaysForGuest: boolean,
  bookingFeePence: number,
): boolean {
  if (!event || ticketPrice <= 0 || inviterPaysForGuest) return false;
  return getInvoiceCreditRecoveryPounds(event, ticketPrice, bookingFeePence) > 0;
}
